import { Box, Typography, Card, CardContent, Grid, Chip, CircularProgress, Link } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import type { GridColDef, GridRenderCellParams, GridRowParams } from '@mui/x-data-grid';
import { useQuery } from '@tanstack/react-query';
import { useAdminTenants } from '@diggens/shared-hooks';
import { authenticatedFetch } from '@diggens/api-client';
import { useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';

export function TenantList() {
  const { data: tenants, isLoading } = useAdminTenants();
  const navigate = useNavigate();
  const [costPeriod, setCostPeriod] = useState<'30d' | '90d' | '1y' | 'all'>('30d');

  // Fetch tenant costs
  const { data: tenantCosts } = useQuery({
    queryKey: ['tenant-costs'],
    queryFn: async () => {
      const response = await authenticatedFetch('/admin/tenant-costs');
      if (!response.ok) throw new Error('Failed to fetch tenant costs');
      return response.json();
    },
    refetchInterval: 60000,
  });

  // Fetch system health
  const { data: health, dataUpdatedAt: healthUpdatedAt } = useQuery({
    queryKey: ['admin-health'],
    queryFn: async () => {
      const response = await authenticatedFetch('/admin/health');
      if (!response.ok) throw new Error('Failed to fetch health');
      return response.json();
    },
    refetchInterval: 30000,
  });

  // Fetch metrics with period filter
  const { data: metrics } = useQuery({
    queryKey: ['admin-metrics', costPeriod],
    queryFn: async () => {
      const response = await authenticatedFetch(`/admin/metrics?period=${costPeriod}`);
      if (!response.ok) throw new Error('Failed to fetch metrics');
      return response.json();
    },
    refetchInterval: 60000,
  });

  const getStatusColor = (status: string) => {
    if (status === 'ok' || status === 'healthy') return 'success';
    if (status === 'error') return 'error';
    return 'warning';
  };

  const formatLastUpdate = (timestamp: number) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    });
  };

  // Merge tenant data with cost data
  const tenantsWithCosts = tenants?.map((tenant) => {
    const costData = tenantCosts?.find((c: any) => c.tenant_id === tenant.id);
    return {
      ...tenant,
      total_runs: costData?.total_runs || 0,
      total_cost: costData?.total_cost || 0,
    };
  });

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Tenant Name',
      flex: 1,
      minWidth: 200,
      // A real link, so the tenant page opens from the keyboard too; the grid's tabIndex lets arrow-key focus land on it.
      // Stopping the click keeps the row click from navigating a second time.
      renderCell: (params: GridRenderCellParams) => (
        <Link component={RouterLink} to={`/super-admin/tenants/${params.row.id}`} tabIndex={params.tabIndex} onClick={(event) => event.stopPropagation()} color="inherit" underline="hover">
          {params.value}
        </Link>
      ),
    },
    {
      field: 'workspace_slug',
      headerName: 'Workspace Slug',
      flex: 1,
      minWidth: 150,
    },
    {
      field: 'total_runs',
      headerName: 'Runs (30d)',
      width: 120,
      type: 'number',
    },
    {
      field: 'total_cost',
      headerName: 'Cost (30d)',
      width: 120,
      valueFormatter: (value) => `$${Number(value).toFixed(2)}`,
    },
    {
      field: 'created_at',
      headerName: 'Created',
      width: 120,
      valueFormatter: (value) => {
        return new Date(value).toLocaleDateString();
      },
    },
  ];

  const handleRowClick = (params: GridRowParams) => {
    navigate(`/super-admin/tenants/${params.row.id}`);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ mb: 3 }}>
        Super Admin
      </Typography>

      {/* System Observability */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">System Health & Metrics</Typography>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              {(['30d', '90d', '1y', 'all'] as const).map((period) => (
                <Chip
                  key={period}
                  label={period === 'all' ? 'All' : period.toUpperCase()}
                  size="small"
                  onClick={() => setCostPeriod(period)}
                  color={costPeriod === period ? 'primary' : 'default'}
                  variant={costPeriod === period ? 'filled' : 'outlined'}
                />
              ))}
            </Box>
            <Typography variant="caption" color="text.secondary">
              Last updated: {healthUpdatedAt ? formatLastUpdate(healthUpdatedAt) : 'Loading...'}
            </Typography>
          </Box>
        </Box>

        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Card>
              <CardContent>
                <Typography variant="subtitle2" gutterBottom>
                  System Status
                </Typography>
                {health ? (
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Chip label={`API: ${health.checks.api.status}`} color={getStatusColor(health.checks.api.status)} size="small" />
                    <Chip label={`DB: ${health.checks.database.status}`} color={getStatusColor(health.checks.database.status)} size="small" />
                    <Chip label={`OpenAI: ${health.checks.openai.status}`} color={getStatusColor(health.checks.openai.status)} size="small" />
                    <Chip label={`Stripe: ${health.checks.stripe.status}`} color={getStatusColor(health.checks.stripe.status)} size="small" />
                  </Box>
                ) : (
                  <CircularProgress size={20} />
                )}
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, sm: 4, md: 2 }}>
            <Card>
              <CardContent>
                <Typography variant="caption" color="text.secondary">
                  LLM Cost (Today)
                </Typography>
                <Typography variant="h6">${metrics?.llmCosts.today.toFixed(2) || '0.00'}</Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, sm: 4, md: 2 }}>
            <Card>
              <CardContent>
                <Typography variant="caption" color="text.secondary">
                  LLM Cost ({costPeriod === 'all' ? 'All Time' : costPeriod.toUpperCase()})
                </Typography>
                <Typography variant="h6">${metrics?.llmCosts.period?.toFixed(2) || '0.00'}</Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, sm: 4, md: 2 }}>
            <Card>
              <CardContent>
                <Typography variant="caption" color="text.secondary">
                  Errors ({costPeriod === 'all' ? 'All' : costPeriod.toUpperCase()})
                </Typography>
                <Typography variant="h6" color={metrics?.errors.today ? 'error' : 'inherit'}>
                  {metrics?.errors.today || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>

      {/* Tenants Table */}
      <Typography variant="h6" gutterBottom>
        Tenants
      </Typography>

      <Box sx={{ height: 600, width: '100%' }}>
        <DataGrid
          rows={tenantsWithCosts || []}
          columns={columns}
          loading={isLoading}
          onRowClick={handleRowClick}
          pageSizeOptions={[10, 25, 50]}
          initialState={{
            pagination: {
              paginationModel: { pageSize: 25 },
            },
          }}
          sx={{
            '& .MuiDataGrid-row': {
              cursor: 'pointer',
            },
          }}
        />
      </Box>
    </Box>
  );
}
